import Hashids from 'hashids'

import {
	BundleFull,
	Form,
	GetAppShowResponse,
	GetBadgeGroupsShowResponse,
	GetCollectionItemsIndexResponse,
	GetCollectionsShowResponse,
	GetMediaItemsShowResponse,
	GetQuizzesShowResponse,
	GetScreensShowResponse,
	Manifest,
	Theme,
} from 'mytours-read-api-typescript'

import { ManifestFull } from './types'

export default class STQRYBunnyReadApi {
	SIMILAR_LANGUAGES = {
		en: ['en', 'en-GB', 'en-AU', 'en-CA', 'en-IN', 'en-SG'],
		es: ['es', 'es-419', 'es-MX', 'es-US'],
		pt: ['pt', 'pt-BR', 'pt-PT'],
		fr: ['fr', 'fr-CA', 'fr-FR'],
	}

	host: string
	parentProjectId: number
	tokenType: string
	tokenUUID: string
	hashids: Hashids

	constructor(host: string, parentProjectId: number, tokenType: string, tokenUUID: string, publishingCryptKey: string) {
		this.host = host
		this.parentProjectId = parentProjectId
		this.tokenType = tokenType
		this.tokenUUID = tokenUUID
		// `hFCSfUiIHtuTsc` seems to be the default Ruby `seps`
		this.hashids = new Hashids(
			publishingCryptKey,
			10,
			'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
			'hFCSfUiIHtuTsc',
		)
	}

	async fetchApp(accountId: number): Promise<GetAppShowResponse> {
		return this.fetchResource(accountId, `/app.json?t=${Date.now()}`)
	}

	async fetchManifest(accountId: number): Promise<Manifest> {
		return this.fetchResource(accountId, `/manifest.json?t=${Date.now()}`)
	}

	/**
	 * The manifest with the offline `cached` flags on it. Published beside the summary but never served
	 * by the read API, so it is fetched for its flags and read against the summary for account ids.
	 */
	async fetchManifestFull(accountId: number): Promise<ManifestFull> {
		return this.fetchResource(accountId, `/manifest_full.json?t=${Date.now()}`)
	}

	/** A collection's own published subtree: everything under this one tour, and nothing else. */
	async fetchCollectionManifest(accountId: number, collectionId: number): Promise<Manifest> {
		return this.fetchResource(
			accountId,
			`/collections/${this.hashids.encode(collectionId)}/manifest.json?t=${Date.now()}`,
		)
	}

	async fetchTheme(accountId: number): Promise<Theme> {
		return this.fetchResource(accountId, `/theme.json?t=${Date.now()}`)
	}

	async fetchScreen(
		accountId: number,
		screenId: number,
		screenVersion: number,
		language: string,
	): Promise<GetScreensShowResponse> {
		const [path, ...fallbackPaths] = this.resourcePaths('screens', screenId, screenVersion, language)
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	async fetchCollection(
		accountId: number,
		collectionId: number,
		collectionVersion: number,
		language: string,
	): Promise<GetCollectionsShowResponse> {
		const [path, ...fallbackPaths] = this.resourcePaths('collections', collectionId, collectionVersion, language)
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	/**
	 * Published under the bundle's own account rather than the app's — a guide client's bundle is written
	 * into the guide's package space but keyed by the client that owns it, which is what makes it reachable
	 * when the client lives in another region.
	 */
	async fetchBundle(accountId: number, bundleId: number, bundleVersion: number, language: string): Promise<BundleFull> {
		const [path, ...fallbackPaths] = this.resourcePaths('bundles', bundleId, bundleVersion, language)
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	async fetchCollectionItems(
		accountId: number,
		collectionId: number,
		collectionVersion: number,
		language: string,
	): Promise<GetCollectionItemsIndexResponse> {
		const [path, ...fallbackPaths] = this.collectionIndexPaths(collectionId, collectionVersion, language, 'items')
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	async fetchMapRoute(accountId: number, mapRouteId: number, mapRouteVersion: number, language: string): Promise<Form> {
		const [path, ...fallbackPaths] = this.resourcePaths('map_routes', mapRouteId, mapRouteVersion, language)
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	async fetchMediaItem(
		accountId: number,
		mediaItemId: number,
		mediaItemVersion: number,
		language: string,
	): Promise<GetMediaItemsShowResponse> {
		const [path, ...fallbackPaths] = this.resourcePaths('media_items', mediaItemId, mediaItemVersion, language)
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	async fetchQuiz(
		accountId: number,
		quizId: number,
		quizVersion: number,
		language: string,
	): Promise<GetQuizzesShowResponse> {
		const [path, ...fallbackPaths] = this.resourcePaths('quizzes', quizId, quizVersion, language)
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	async fetchBadgeGroup(
		accountId: number,
		badgeGroupId: number,
		badgeGroupVersion: number,
		language: string,
	): Promise<GetBadgeGroupsShowResponse> {
		const [path, ...fallbackPaths] = this.resourcePaths('badge_groups', badgeGroupId, badgeGroupVersion, language)
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	async fetchForm(accountId: number, formId: number, formVersion: number, language: string): Promise<Form> {
		const [path, ...fallbackPaths] = this.resourcePaths('crm/forms', formId, formVersion, language)
		return this.fetchResource(accountId, path, fallbackPaths)
	}

	/**
	 * Every URL the client would try for one published resource, in the order `fetchResource` tries them:
	 * the requested language, then the similar languages, then the untagged primary. The downloader stores
	 * whichever of them answers, so an item published only in the fallback language is still there offline.
	 */
	resourceUrls(accountId: number, folder: string, id: number, version: number, language: string): string[] {
		return this.resourcePaths(folder, id, version, language).map(path => this.resourceUrl(accountId, path))
	}

	/** The same, for a collection's `items` and `triggers` indexes, which hang off the collection's own folder. */
	collectionIndexUrls(
		accountId: number,
		collectionId: number,
		version: number,
		language: string,
		index: 'items' | 'triggers',
	): string[] {
		return this.collectionIndexPaths(collectionId, version, language, index).map(path =>
			this.resourceUrl(accountId, path),
		)
	}

	/** The project theme, which is published at the project root rather than under an id. */
	themeUrls(accountId: number, language: string): string[] {
		const builder = this.createLanguagePathBuilder('/theme{language}.json')
		return [builder(language), ...this.getFallbackPaths(builder, language)].map(path =>
			this.resourceUrl(accountId, path),
		)
	}

	private collectionIndexPaths(
		collectionId: number,
		version: number,
		language: string,
		index: 'items' | 'triggers',
	): string[] {
		const builder = this.createLanguagePathBuilder(
			`/collections/${this.hashids.encode(collectionId)}/${index}/metadata{language}.json?t=${version}`,
		)
		return [builder(language), ...this.getFallbackPaths(builder, language)]
	}

	private resourcePaths(folder: string, id: number, version: number, language: string): string[] {
		const builder = this.createLanguagePathBuilder(
			`/${folder}/${this.hashids.encode(id)}/metadata{language}.json?t=${version}`,
		)
		return [builder(language), ...this.getFallbackPaths(builder, language)]
	}

	private resourceUrl(accountId: number, path: string): string {
		return `${this.host}/${this.projectAndAccountPrefix(accountId)}${path}`
	}

	private createLanguagePathBuilder(templatedPath: string) {
		return (language?: string) => {
			return templatedPath.replace('{language}', language ? `-${language}` : '')
		}
	}

	private getFallbackPaths(languagePathBuilder: (language?: string) => string, language: string) {
		const baseLanguage = language.split('-')[0]
		const similarLanguages =
			baseLanguage in this.SIMILAR_LANGUAGES
				? this.SIMILAR_LANGUAGES[baseLanguage as keyof typeof this.SIMILAR_LANGUAGES]
				: []
		const fallbackPaths = similarLanguages.filter(lang => lang !== language).map(lang => languagePathBuilder(lang))
		fallbackPaths.push(languagePathBuilder())
		return fallbackPaths
	}

	private async fetchResource(accountId: number, path: string, fallbackPaths: string[] = []) {
		const resourceResponse = await this.fetch(this.resourceUrl(accountId, path))

		if (resourceResponse.status === 200) {
			return resourceResponse.json()
		}

		if (fallbackPaths.length === 0) {
			throw resourceResponse
		}

		let fallbackResponse: Response | undefined
		for (const fallbackPath of fallbackPaths) {
			// Fallback to the primary language if the requested language is not available
			fallbackResponse = await this.fetch(this.resourceUrl(accountId, fallbackPath))
			if (fallbackResponse.status === 200) {
				return fallbackResponse.json()
			}
		}

		throw fallbackResponse
	}

	private projectAndAccountPrefix(accountId: number) {
		return `${this.hashids.encode(this.parentProjectId)}/${this.hashids.encode(accountId)}`
	}

	private fetch(input: RequestInfo | URL, init: RequestInit = {}) {
		return fetch(input, {
			...init,
			headers: {
				...(init.headers || {}),
				Authorization: 'Basic ' + btoa(this.parentProjectId + ':' + this.tokenUUID),
				'stqry-project-type': this.tokenType,
			},
		})
	}
}
